<template>
  <div class="custom-popover-container">
    <div ref="triggerRef" class="popover-trigger" @click.stop="togglePopover">
      <slot name="trigger"></slot>
    </div>

    <!-- 全屏蒙层 -->
    <transition name="fade">
      <div
        v-if="isVisible"
        class="popover-overlay"
        @click="handleOverlayClick"
      ></div>
    </transition>

    <transition name="fade">
      <div
        v-if="isVisible"
        ref="popoverRef"
        class="popover-content"
        :style="popoverStyle"
      >
        <div class="popover-arrow" :style="arrowStyle"></div>
        <div class="popover-inner">
          <slot name="content"></slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";

const props = defineProps({
  placement: {
    type: String,
    default: "bottom",
    validator: (value) => ["top", "bottom", "left", "right"].includes(value),
  },
  offset: {
    type: Number,
    default: 8,
  },
  closeOnClickOverlay: {
    type: Boolean,
    default: true,
  },
  lockScroll: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["show", "hide"]);

const isVisible = ref(false);
const triggerRef = ref(null);
const popoverRef = ref(null);
const scrollbarWidth = ref(0);

const togglePopover = () => {
  isVisible.value = !isVisible.value;
  emit(isVisible.value ? "show" : "hide");

  if (props.lockScroll) {
    if (isVisible.value) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
  }
};

const lockBodyScroll = () => {
  // 计算并存储滚动条宽度
  scrollbarWidth.value =
    window.innerWidth - document.documentElement.clientWidth;
  document.body.style.overflow = "hidden";
  document.body.style.paddingRight = `${scrollbarWidth.value}px`;
};

const unlockBodyScroll = () => {
  document.body.style.overflow = "";
  document.body.style.paddingRight = "";
};

const handleClickOutside = (event) => {
  if (
    isVisible.value &&
    popoverRef.value &&
    !popoverRef.value.contains(event.target) &&
    !triggerRef.value.contains(event.target)
  ) {
    closePopover();
  }
};

const closePopover = () => {
  isVisible.value = false;
  emit("hide");
  if (props.lockScroll) {
    unlockBodyScroll();
  }
};

const handleOverlayClick = () => {
  if (props.closeOnClickOverlay) {
    closePopover();
  }
};

const popoverStyle = computed(() => {
  if (!triggerRef.value || !isVisible.value) return {};

  const triggerRect = triggerRef.value.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const popoverWidth = Math.min(890, viewportWidth - 40); // 不超过890px且留出边距
  const popoverHeight = popoverRef.value?.clientHeight || 0;

  const styles = {
    position: "fixed",
    zIndex: 1000,
    width: `${popoverWidth}px`,
    maxWidth: "calc(100vw - 40px)", // 确保两侧有20px边距
  };

  // 计算水平位置 - 限制在1400px安全区内
  const safeAreaWidth = Math.min(1400, viewportWidth);
  const safeAreaLeft = (viewportWidth - safeAreaWidth) / 2;

  let left = triggerRect.left + triggerRect.width / 2 - popoverWidth / 2;

  // 限制在安全区内
  left = Math.max(safeAreaLeft + 20, left); // 左边距不小于安全区左侧+20px
  left = Math.min(left, safeAreaLeft + safeAreaWidth - popoverWidth - 20); // 右边距不小于安全区右侧-20px

  // 计算垂直位置
  if (props.placement === "bottom") {
    const top = triggerRect.bottom + props.offset;
    styles.top = `${Math.min(top, viewportHeight - popoverHeight - 20)}px`; // 底部留20px
  } else if (props.placement === "top") {
    const bottom = viewportHeight - triggerRect.top + props.offset;
    styles.bottom = `${Math.min(bottom, viewportHeight - 20)}px`; // 顶部留20px
  }

  styles.left = `${left}px`;

  return styles;
});
const handleResize = () => {
  if (isVisible.value) {
    isVisible.value = false;
    nextTick(() => {
      isVisible.value = true;
    });
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("resize", handleResize);
  // 确保组件卸载时解锁滚动
  unlockBodyScroll();
});
</script>

<style scoped>
.custom-popover-container {
  display: inline-block;
  position: relative;
}

.popover-trigger {
  cursor: pointer;
}

.popover-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.popover-content {
  position: fixed;
  background: white;
  /* border-radius: 4px; */
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  padding: 20px 12px;
  width: 890px;
  max-width: calc(100vw - 40px);
  z-index: 1000;
  transform: none !important;

  /* 限制在1400px安全区内 */
  @media (min-width: 1440px) {
    left: 50% !important;
    transform: translateX(-50%) !important;
    margin-left: 0;
    max-width: 1400px;
  }
}
.popover-content[data-placement="bottom"] .popover-arrow {
  top: -10px;
  left: 50%;
  margin-left: -5px;
  border-width: 0 10px 10px;
  border-color: transparent transparent white transparent;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
